import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { Toast } from '@/components/Toast';
import { detectBrand } from '@/lib/brand';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

type Props = {
  user: MeResponse;
  onLogout: () => void;
  /** Vysunuté menu na mobilu (od `lg` výš se ignoruje). */
  open?: boolean;
  onClose?: () => void;
};

const itemBase =
  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors';
const itemInactive = 'text-text-secondary hover:bg-surface-hover hover:text-text';
const itemActive = 'bg-primary-50 text-primary';
const sectionLabel = 'text-[11px] font-semibold text-text-muted uppercase tracking-wider px-3 py-2';

export function Sidebar({ user, onLogout, open = false, onClose }: Props) {
  // Sidebar skrývá moduly, ke kterým uživatel nemá přístup. Per-modul check
  // přes hasModule() — role pole už nepoužíváme pro autorizaci.
  const mods = user.modules ?? [];
  // Na vyroba.v-martsystem.cz je to samostatná aplikace — jiné jméno v hlavičce
  // a ostatní tools se do sidebaru nevykreslují, i když k nim uživatel přístup
  // má. Sdílí se kód a databáze, ne navigace. Viz src/lib/brand.ts.
  const brand = detectBrand();
  const isVyrobaBrand = brand.id === 'vyroba';
  const inTools = (m: Parameters<typeof hasModule>[1]) => !isVyrobaBrand && hasModule(mods, m);
  const canProductsDb = inTools('products_db');
  const canOrdersDb = inTools('orders_db');
  const canPropojeni = inTools('propojeni');
  const canSady = inTools('sady');
  const canUpsell = inTools('upsell');
  const canSkladSync = inTools('sklad_sync');
  const canWatchdog = inTools('watchdog');
  const canSalesHistory = inTools('sales_history');
  const canAutomatizaceObjednavek = inTools('automatizace_objednavek');
  const canRozdilySkladu = inTools('rozdily_skladu');
  const canRecenzeSync = inTools('recenze_sync');
  const canVyroba = hasModule(mods, 'vyroba');
  // Části výroby — každá položka menu se povoluje zvlášť.
  const canZakazky = hasModule(mods, 'vyroba_zakazky');
  const canTerminy = hasModule(mods, 'vyroba_terminy');
  const canPlanStrihani = hasModule(mods, 'vyroba_plan_strihani');
  const canSkladLatek = hasModule(mods, 'vyroba_sklad_latek');
  const canInfoLatka = hasModule(mods, 'vyroba_info_latka');
  const canKontrolaNapletu = hasModule(mods, 'vyroba_kontrola_napletu');
  const canKontrolaLatek = hasModule(mods, 'vyroba_kontrola_latek');
  const canDelkaZakazek = hasModule(mods, 'vyroba_delka_zakazek');
  const canVyskladneni = hasModule(mods, 'vyroba_vyskladneni');
  const canNaZakazku = hasModule(mods, 'vyroba_na_zakazku');
  const canPrijem = hasModule(mods, 'vyroba_prijem');
  const canObjednavky = hasModule(mods, 'vyroba_objednavky');
  const canSkladMaterialu = hasModule(mods, 'vyroba_sklad_materialu');
  const canExterniVera = hasModule(mods, 'vyroba_externi_vera');
  const canNakupPrehled = hasModule(mods, 'vyroba_nakup');
  const canSici = hasModule(mods, 'vyroba_sici');
  const canVykazy = hasModule(mods, 'vyroba_vykazy');
  const canDilnaPlan = hasModule(mods, 'vyroba_dilna_plan');
  const canChybovost = hasModule(mods, 'vyroba_chybovost');
  const canZadani = hasModule(mods, 'vyroba_zadani');
  const canStatistiky = hasModule(mods, 'vyroba_statistiky');
  const canTrideniNaZakazku = hasModule(mods, 'vyroba_trideni_na_zakazku');
  const canLogObjednavek = hasModule(mods, 'vyroba_log_objednavek');
  const canAutomatizace = hasModule(mods, 'vyroba_automatizace');
  const canPozadavky = hasModule(mods, 'vyroba_pozadavky');
  const canFormulare = hasModule(mods, 'vyroba_formulare');
  const canDruhaJakost = hasModule(mods, 'vyroba_druha_jakost');
  const canNaskladneni = hasModule(mods, 'vyroba_naskladneni');
  const canEshopy = hasModule(mods, 'vyroba_eshopy');
  const zdroje: { to: string; label: string; can: boolean }[] = [
    {
      to: 'kategorie',
      label: 'Databáze kategorií',
      can: hasModule(mods, 'vyroba_zdroje_kategorie'),
    },
    { to: 'niti', label: 'Databáze nití', can: hasModule(mods, 'vyroba_zdroje_niti') },
    { to: 'zipy', label: 'Databáze zipů', can: hasModule(mods, 'vyroba_zdroje_zipy') },
    { to: 'latky', label: 'Databáze látek', can: hasModule(mods, 'vyroba_zdroje_latky') },
    { to: 'produkty', label: 'Databáze produktů', can: hasModule(mods, 'vyroba_zdroje_produkty') },
    { to: 'napletu', label: 'Databáze nápletů', can: hasModule(mods, 'vyroba_zdroje_napletu') },
    {
      to: 'svadlenky',
      label: 'Databáze švadlenek',
      can: hasModule(mods, 'vyroba_zdroje_svadlenky'),
    },
    { to: 'normy', label: 'Normy švadlenek', can: hasModule(mods, 'vyroba_zdroje_normy') },
  ];
  const canZdroje = zdroje.some((z) => z.can);
  const canUserAdmin = inTools('user_admin');
  // Na doméně výroby se Tools nevykreslují — odkaz na správu uživatelů ale ano.
  const canUserAdminAnywhere = hasModule(mods, 'user_admin');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inSkladSync = pathname.startsWith('/sklad-sync');
  const inPropojeni = pathname.startsWith('/propojeni-produktu');
  const inSady = pathname.startsWith('/produktove-sady');
  const inUpsell = pathname.startsWith('/upsell-cross-sell');
  const inZdroje = pathname.startsWith('/vyroba/zdroje');
  const inStriharna =
    pathname.startsWith('/vyroba/striharna') || pathname === '/vyroba/sklad-latek';
  const inDilna = pathname.startsWith('/vyroba/dokoncovaci-dilna');
  const inSici = pathname.startsWith('/vyroba/sici-dilna');
  const inExterni = pathname.startsWith('/vyroba/externi');
  const inNakupci =
    pathname.startsWith('/vyroba/sklad-latek/objednavky') ||
    pathname.startsWith('/vyroba/sklad-latek/prijem') ||
    pathname.startsWith('/vyroba/nakup');
  // Sekce "Sdílená data" se zobrazí, jen když má uživatel aspoň jeden z těch modulů.
  const showSharedData = canProductsDb || canOrdersDb;
  // Self-service změna hesla
  const [showChangePw, setShowChangePw] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  // Rozbalovací sekce „Zdroje" (zrcadla z Tabidoo). Otevřená, když jsi v ní.
  const [zdrojeOpen, setZdrojeOpen] = useState(inZdroje);
  // Rozbalovací sekce „Střihárna" (plán stříhání + sklad látek).
  const [striharnaOpen, setStriharnaOpen] = useState(inStriharna);
  // Rozbalovací sekce „Dokončovací dílna" (zatím jen plán).
  const [dilnaOpen, setDilnaOpen] = useState(inDilna);
  // Rozbalovací sekce „Výroba" (zakázky + jejich termíny a zadání).
  const [vyrobaOpen, setVyrobaOpen] = useState(
    pathname.startsWith('/vyroba/terminy') ||
      pathname.startsWith('/vyroba/zadani') ||
      pathname.startsWith('/vyroba/statistiky') ||
      pathname.startsWith('/vyroba/trideni-na-zakazku') ||
      pathname.startsWith('/vyroba/log-objednavek'),
  );
  // Rozbalovací sekce „Šicí dílna" (obrazovka pro čtečku).
  const [siciOpen, setSiciOpen] = useState(inSici);
  // Rozbalovací sekce „Externí dílny" (výpis zakázek externích švadlen).
  const [externiOpen, setExterniOpen] = useState(inExterni);
  // Rozbalovací sekce „Nákup" (příjem a objednávky materiálu u dodavatelů).
  const [nakupciOpen, setNakupciOpen] = useState(inNakupci);

  // Po přechodu na jinou stránku zasuň menu (na mobilu by jinak zůstalo přes obsah).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reagujeme právě na změnu cesty
  useEffect(() => {
    onClose?.();
  }, [pathname]);

  return (
    <aside
      className={`w-60 shrink-0 bg-surface border-r border-border flex flex-col h-screen overflow-y-auto fixed inset-y-0 left-0 z-40 transition-transform lg:sticky lg:top-0 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <NavLink
        to={brand.homePath}
        className="px-5 py-5 flex items-center gap-3 border-b border-border hover:bg-surface-hover transition-colors"
      >
        <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 7l9-4 9 4-9 4-9-4z" />
            <path d="M3 7v10l9 4 9-4V7" />
            <path d="M12 11v10" />
          </svg>
        </div>
        <div>
          <div className="text-sm font-semibold text-text leading-tight">{brand.name}</div>
          <div className="text-[11px] text-text-muted">{brand.domain}</div>
        </div>
      </NavLink>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {!isVyrobaBrand && (
          <>
            <div className={sectionLabel}>Domů</div>
            <NavLink
              to="/"
              end
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M3 12l9-9 9 9M5 10v10h14V10" />
              Přehled nástrojů
            </NavLink>
          </>
        )}

        {canVyroba && (
          <>
            <div className={isVyrobaBrand ? sectionLabel : `${sectionLabel} mt-4`}>Výroba</div>
            {(canZakazky ||
              canTerminy ||
              canZadani ||
              canNaZakazku ||
              canVyskladneni ||
              canStatistiky ||
              canTrideniNaZakazku ||
              canLogObjednavek) && (
              <>
                {/* Zakázky jsou srdce modulu — položka je výrazná a rozbaluje
                    své podstránky; klik na ni vede rovnou na seznam zakázek. */}
                <div className="flex items-stretch gap-1">
                  {canZakazky ? (
                    <NavLink
                      to="/vyroba/vyroba"
                      className={({ isActive }) =>
                        `${itemBase} flex-1 text-[15px] font-bold bg-primary text-white hover:bg-primary-dark ${
                          isActive ? 'bg-primary-dark ring-2 ring-primary-100' : ''
                        }`
                      }
                    >
                      <Icon path="M3 21h18M5 21V10l5 3V10l5 3V7l4 2v12" />
                      Výroba
                    </NavLink>
                  ) : (
                    <span
                      className={`${itemBase} flex-1 text-[15px] font-bold bg-primary text-white`}
                    >
                      <Icon path="M3 21h18M5 21V10l5 3V10l5 3V7l4 2v12" />
                      Výroba
                    </span>
                  )}
                  {(canTerminy ||
                    canZadani ||
                    canNaZakazku ||
                    canVyskladneni ||
                    canStatistiky ||
                    canTrideniNaZakazku ||
                    canLogObjednavek) && (
                    <button
                      type="button"
                      onClick={() => setVyrobaOpen((o) => !o)}
                      aria-label={vyrobaOpen ? 'Sbalit' : 'Rozbalit'}
                      className="shrink-0 rounded-lg bg-primary text-white px-2 hover:bg-primary-dark transition-colors cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className={`transition-transform ${vyrobaOpen ? 'rotate-90' : ''}`}
                      >
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </button>
                  )}
                </div>
                {vyrobaOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    {canTerminy && (
                      <NavLink
                        to="/vyroba/terminy"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                        Termíny výroby
                      </NavLink>
                    )}
                    {canZadani && (
                      <NavLink
                        to="/vyroba/zadani"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M20 8v13H4V8M2 3h20v5H2zM10 12h4" />
                        Zadání do výroby
                      </NavLink>
                    )}
                    {canVyskladneni && (
                      <NavLink
                        to="/vyroba/striharna/vyskladneni"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 6h18M8 6V4h8v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14M10 11v6M14 11v6" />
                        Vyskladnění z výroby
                      </NavLink>
                    )}
                    {canNaZakazku && (
                      <NavLink
                        to="/vyroba/striharna/na-zakazku"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M12 3v18M5 8l7-5 7 5M5 8v8l7 5 7-5V8" />
                        Šití na zakázku
                      </NavLink>
                    )}
                    {canStatistiky && (
                      <NavLink
                        to="/vyroba/statistiky"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 3v18h18M7 15l4-4 3 3 5-6" />
                        Statistiky
                      </NavLink>
                    )}
                    {canTrideniNaZakazku && (
                      <NavLink
                        to="/vyroba/trideni-na-zakazku"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 6h18M3 12h18M3 18h18" />
                        Třídění objednávek na zakázku
                      </NavLink>
                    )}
                    {canLogObjednavek && (
                      <NavLink
                        to="/vyroba/log-objednavek"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4" />
                        Log automatizace objednávek
                      </NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            {(canPlanStrihani ||
              canSkladLatek ||
              canInfoLatka ||
              canKontrolaNapletu ||
              canKontrolaLatek ||
              canDelkaZakazek) && (
              <>
                <button
                  type="button"
                  onClick={() => setStriharnaOpen((o) => !o)}
                  className={`${itemBase} ${inStriharna ? itemActive : itemInactive} w-full text-left cursor-pointer`}
                >
                  <Icon path="M9 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12" />
                  Střihárna
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${striharnaOpen ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                {striharnaOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    {canPlanStrihani && (
                      <NavLink
                        to="/vyroba/striharna"
                        end
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M9 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12" />
                        Plán stříhání
                      </NavLink>
                    )}
                    {canSkladLatek && (
                      <NavLink
                        to="/vyroba/sklad-latek"
                        end
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 9.5 12 3l9 6.5M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10M9 21v-6h6v6" />
                        Sklad látek
                      </NavLink>
                    )}
                    {canInfoLatka && (
                      <NavLink
                        to="/vyroba/striharna/latka"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM21 21l-4.35-4.35" />
                        Informace o látce
                      </NavLink>
                    )}
                    {canKontrolaNapletu && (
                      <NavLink
                        to="/vyroba/striharna/naplet"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 12h4l3-8 4 16 3-8h4" />
                        Kontrola nápletu
                      </NavLink>
                    )}
                    {canKontrolaLatek && (
                      <NavLink
                        to="/vyroba/striharna/latky"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M4 6h16M4 12h16M4 18h10" />
                        Kontrola látek
                      </NavLink>
                    )}
                    {canDelkaZakazek && (
                      <NavLink
                        to="/vyroba/striharna/delka-zakazek"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M12 6v6l4 2M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z" />
                        Délka zakázek švadlen
                      </NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            {(canObjednavky || canPrijem || canSkladMaterialu || canNakupPrehled) && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    // „Nákup" rovnou otevře dashboard (kdo na něj má právo) a rozbalí
                    // podsekce; na samotném dashboardu klik menu jen zasune/vysune.
                    if (canNakupPrehled && pathname !== '/vyroba/nakup') {
                      navigate('/vyroba/nakup');
                      setNakupciOpen(true);
                    } else {
                      setNakupciOpen((o) => !o);
                    }
                  }}
                  className={`${itemBase} ${inNakupci ? itemActive : itemInactive} w-full text-left cursor-pointer`}
                >
                  <Icon path="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" />
                  Nákup
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${nakupciOpen ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                {nakupciOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    {canPrijem && (
                      <NavLink
                        to="/vyroba/sklad-latek/prijem"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M1 3h15v13H1zM16 8h4l3 3v5h-7V8zM5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5" />
                        Příjem materiálu
                      </NavLink>
                    )}
                    {canObjednavky && (
                      <NavLink
                        to="/vyroba/sklad-latek/objednavky"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1zM8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 12h6M9 16h6" />
                        Objednávky materiálu
                      </NavLink>
                    )}
                    {canSkladMaterialu && (
                      <NavLink
                        to="/vyroba/nakup/material"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8" />
                        Sklad drobného materiálu
                      </NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            {canExterniVera && (
              <>
                <button
                  type="button"
                  onClick={() => setExterniOpen((o) => !o)}
                  className={`${itemBase} ${inExterni ? itemActive : itemInactive} w-full text-left cursor-pointer`}
                >
                  <Icon path="M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6" />
                  Externí dílny
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${externiOpen ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                {externiOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    <NavLink
                      to="/vyroba/externi/vera"
                      className={({ isActive }) =>
                        `${itemBase} ${isActive ? itemActive : itemInactive}`
                      }
                    >
                      <Icon path="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
                      Věra Martínková
                    </NavLink>
                  </div>
                )}
              </>
            )}
            {(canSici || canVykazy) && (
              <>
                <button
                  type="button"
                  onClick={() => setSiciOpen((o) => !o)}
                  className={`${itemBase} ${inSici ? itemActive : itemInactive} w-full text-left cursor-pointer`}
                >
                  <Icon path="M12 3v13M9 6l3-3 3 3M7.5 16a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M16.5 16a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M9.5 18.5h5" />
                  Šicí dílna
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${siciOpen ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                {siciOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    {canSici && (
                      <NavLink
                        to="/vyroba/sici-dilna"
                        end
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 21h18M5 21V10l5 3V10l5 3V7l4 2v12" />
                        Výroba
                      </NavLink>
                    )}
                    {canVykazy && (
                      <NavLink
                        to="/vyroba/sici-dilna/vykazy"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" />
                        Výkazy
                      </NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            {(canDilnaPlan || canChybovost || canNaskladneni) && (
              <>
                <button
                  type="button"
                  onClick={() => setDilnaOpen((o) => !o)}
                  className={`${itemBase} ${inDilna ? itemActive : itemInactive} w-full text-left cursor-pointer`}
                >
                  <Icon path="M6 2v9a6 6 0 0 0 12 0V2M4 22h16M12 17v5" />
                  Dokončovací dílna
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${dilnaOpen ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                {dilnaOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    {canDilnaPlan && (
                      <NavLink
                        to="/vyroba/dokoncovaci-dilna"
                        end
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                        Plán
                      </NavLink>
                    )}
                    {canNaskladneni && (
                      <NavLink
                        to="/vyroba/naskladneni-z-vyroby"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M3 9l1-5h16l1 5M3 9h18M3 9v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9M12 12v6M9 15h6" />
                        Naskladnění z výroby
                      </NavLink>
                    )}
                    {canChybovost && (
                      <NavLink
                        to="/vyroba/dokoncovaci-dilna/chybovost"
                        className={({ isActive }) =>
                          `${itemBase} ${isActive ? itemActive : itemInactive}`
                        }
                      >
                        <Icon path="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        Chybovost švadlen
                      </NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            {canPozadavky && (
              <NavLink
                to="/vyroba/nova-objednavka"
                className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
              >
                <Icon path="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM14 2v6h6M9 13h6M9 17h4" />
                Nová objednávka
              </NavLink>
            )}
            {canFormulare && (
              <NavLink
                to="/vyroba/formulare"
                className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
              >
                <Icon path="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4" />
                Formuláře
              </NavLink>
            )}
            {canDruhaJakost && (
              <NavLink
                to="/vyroba/druha-jakost"
                className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
              >
                <Icon path="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM9 9h.01M15 9h.01M8 15h8" />
                2. jakost
              </NavLink>
            )}
            {canEshopy && (
              <NavLink
                to="/vyroba/eshopy"
                className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
              >
                <Icon path="M3 9l1-5h16l1 5M3 9h18M3 9v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9M9 21v-6h6v6" />
                Napojení e-shopů
              </NavLink>
            )}
            {canZdroje && (
              <>
                <button
                  type="button"
                  onClick={() => setZdrojeOpen((o) => !o)}
                  className={`${itemBase} ${inZdroje ? itemActive : itemInactive} w-full text-left cursor-pointer`}
                >
                  <Icon path="M12 4c4.4 0 8 .9 8 2s-3.6 2-8 2-8-.9-8-2 3.6-2 8-2zM4 6v12c0 1.1 3.6 2 8 2s8-.9 8-2V6M4 12c0 1.1 3.6 2 8 2s8-.9 8-2" />
                  Zdroje
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${zdrojeOpen ? 'rotate-90' : ''}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                {zdrojeOpen && (
                  <div className="ml-4 pl-2 border-l border-border-light flex flex-col">
                    {zdroje
                      .filter((z) => z.can)
                      .map((z) => (
                        <NavLink
                          key={z.to}
                          to={`/vyroba/zdroje/${z.to}`}
                          className={({ isActive }) =>
                            `${itemBase} ${isActive ? itemActive : itemInactive}`
                          }
                        >
                          <Icon path="M4 7c0-1.1.9-2 2-2h12a2 2 0 0 1 2 2M4 7v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7M4 7h16M9 11h6M9 15h6" />
                          {z.label}
                        </NavLink>
                      ))}
                  </div>
                )}
              </>
            )}
            {canAutomatizace && (
              <NavLink
                to="/vyroba/automatizace"
                className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
              >
                <Icon path="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                Automatizace
              </NavLink>
            )}
            {/* Dokumentace je jen pro správce — běžné kolegyně ji nepotřebují. */}
            {canUserAdminAnywhere && (
              <NavLink
                to="/vyroba/dokumentace"
                className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
              >
                <Icon path="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2zM9 7h7M9 11h7" />
                Dokumentace
              </NavLink>
            )}
            {isVyrobaBrand && canUserAdminAnywhere && (
              // Práva se spravují v Tools — z výroby na ně vede aspoň odkaz,
              // ať je nemusí nikdo hledat.
              <a
                href="https://tools.v-martsystem.cz/admin/users"
                target="_blank"
                rel="noopener noreferrer"
                className={`${itemBase} ${itemInactive} mt-2`}
              >
                <Icon path="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                Uživatelé
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="ml-auto text-text-muted"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                </svg>
              </a>
            )}
          </>
        )}

        {showSharedData && <div className={`${sectionLabel} mt-4`}>Sdílená data</div>}
        {canProductsDb && (
          <NavLink
            to="/databaze-produktu"
            className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
          >
            <Icon path="M4 7h16M4 12h16M4 17h16" />
            Databáze produktů
          </NavLink>
        )}
        {canOrdersDb && (
          <NavLink
            to="/databaze-objednavek"
            className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
          >
            <Icon path="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
            Databáze objednávek
          </NavLink>
        )}

        {/* Per-tool sub-navigace — zobrazí se jen když jsme uvnitř toolu */}
        {canPropojeni && inPropojeni && (
          <>
            <div className={`${sectionLabel} mt-4`}>Propojení produktů</div>
            <NavLink
              to="/propojeni-produktu"
              end
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              Seznam propojení
            </NavLink>
          </>
        )}

        {canSady && inSady && (
          <>
            <div className={`${sectionLabel} mt-4`}>Produktové sady</div>
            <NavLink
              to="/produktove-sady"
              end
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              Seznam sad
            </NavLink>
          </>
        )}

        {canUpsell && inUpsell && (
          <>
            <div className={`${sectionLabel} mt-4`}>Upsell &amp; Cross-sell</div>
            <NavLink
              to="/upsell-cross-sell"
              end
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M3 3h2l2.4 12.5a2 2 0 0 0 2 1.5h9.4a2 2 0 0 0 2-1.6L22 7H6" />
              Bannery a nabídky
            </NavLink>
          </>
        )}

        {canSkladSync && inSkladSync && (
          <>
            <div className={`${sectionLabel} mt-4`}>Sklad-Sync</div>
            <NavLink
              to="/sklad-sync"
              end
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M3 7l9-4 9 4-9 4-9-4zM3 7v10l9 4 9-4V7M12 11v10" />
              Dashboard
            </NavLink>
            <NavLink
              to="/sklad-sync/admin/recipients"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M3 5h18v14H3zM3 5l9 8 9-8" />
              Příjemci reportu
            </NavLink>
            <NavLink
              to="/sklad-sync/admin/excluded"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M18 6L6 18M6 6l12 12" />
              Vyřazené produkty
            </NavLink>
          </>
        )}

        {canSalesHistory && (
          <>
            <div className={`${sectionLabel} mt-4`}>Analýza prodeje</div>
            <NavLink
              to="/analyza-historie-prodeje"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M3 3v18h18M7 16v-5M12 16V7M17 16v-8" />
              Historie prodeje
            </NavLink>
          </>
        )}

        {canAutomatizaceObjednavek && (
          <>
            <div className={`${sectionLabel} mt-4`}>Automatizace objednávek</div>
            <NavLink
              to="/automatizace-objednavek"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M12 3v3m0 12v3m9-9h-3M6 12H3m14.66-6.66-2.12 2.12M7.46 16.54l-2.12 2.12m12.32 0-2.12-2.12M7.46 7.46 5.34 5.34" />
              Pravidla
            </NavLink>
          </>
        )}

        {canRozdilySkladu && (
          <>
            <div className={`${sectionLabel} mt-4`}>Rozdíly skladů</div>
            <NavLink
              to="/rozdily-skladu"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M9 3v18M3 9h18M3 15h18" />
              Kontrola
            </NavLink>
          </>
        )}

        {canRecenzeSync && (
          <>
            <div className={`${sectionLabel} mt-4`}>Recenze</div>
            <NavLink
              to="/recenze-sync"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              Sync CZ ↔ SK
            </NavLink>
          </>
        )}

        {canWatchdog && (
          <>
            <div className={`${sectionLabel} mt-4`}>Hlídací pes</div>
            <NavLink
              to="/hlidaci-pes"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M10 5.172C10 3.45 8.423 2 6.5 2 4.577 2 3 3.45 3 5.172c0 .73.291 1.405.79 1.892.5.487 1.13.788 1.838.788h.872c.708 0 1.337-.301 1.838-.788.499-.487.79-1.162.79-1.892zM21 5.172C21 3.45 19.423 2 17.5 2 15.577 2 14 3.45 14 5.172c0 .73.291 1.405.79 1.892.5.487 1.13.788 1.838.788h.872c.708 0 1.337-.301 1.838-.788.499-.487.79-1.162.79-1.892zM12 7v9m0 0l-3-3m3 3l3-3M5 13a7 7 0 0 0 14 0" />
              Přehled a metriky
            </NavLink>
          </>
        )}

        {/* Globální admin (sdílený mezi tools) — vyžaduje user_admin modul */}
        {canUserAdmin && (
          <>
            <div className={`${sectionLabel} mt-4`}>Systém</div>
            <NavLink
              to="/admin/users"
              className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
            >
              <Icon path="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM4 20c0-3 4-5 8-5s8 2 8 5" />
              Uživatelé
            </NavLink>
          </>
        )}
      </nav>

      <div className="px-3 py-3 border-t border-border">
        <div className="px-3 py-2 mb-1">
          <div className="text-sm font-medium text-text truncate">{user.name}</div>
          <div className="text-[11px] text-text-muted truncate">{user.email}</div>
        </div>
        {/* Technický přehled aplikace — užitečný jen pro správce. */}
        {canUserAdminAnywhere && (
          <NavLink
            to="/informace"
            className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemInactive}`}
          >
            <Icon path="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20M12 16v-4M12 8h.01" />
            Informace
          </NavLink>
        )}
        <button
          type="button"
          onClick={() => setShowChangePw(true)}
          className={`${itemBase} ${itemInactive} w-full text-left cursor-pointer`}
        >
          <Icon path="M12 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 2c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z" />
          Změnit heslo
        </button>
        <button
          type="button"
          onClick={onLogout}
          className={`${itemBase} text-text-secondary hover:bg-danger-light hover:text-danger w-full text-left cursor-pointer`}
        >
          <Icon path="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          Odhlásit se
        </button>
      </div>
      {showChangePw && (
        <ChangePasswordModal
          onClose={() => setShowChangePw(false)}
          onSuccess={(msg) => {
            setShowChangePw(false);
            setToast({ msg, kind: 'success' });
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </aside>
  );
}

function Icon({ path }: { path: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}
